import { useCallback, useMemo } from "react";
import { useDateTimeRangeStore } from "@/shared/stores/useDateTimeRangeStore";
import { useBulkSelectionStore } from "../stores/useBulkSelectionStore";
import { useReviewFilterStore } from "../stores/useReviewFilterStore";
import type { ReviewFilters } from "../types/review";
import type { RevealedFilterKey } from "../components/review/review-filter-bar";
import type { ReviewFilterSnapshot } from "../utils/filter-presets";

/**
 * Is anything refined beyond scope + window?
 *
 * Drives the Reset button and the "no results because of filters" empty state,
 * so it must list EVERY refinement key — a missing one silently tells the user
 * their empty grid is an empty scope.
 */
const hasAnyRefinement = (
  f: Omit<ReviewFilters, "start_time" | "end_time">,
): boolean =>
  !!f.label_in ||
  !!f.review_state_in ||
  !!f.event_state_in ||
  !!f.review_presence ||
  f.min_confidence != null ||
  f.max_confidence != null ||
  !!f.reviewed_by ||
  !!f.reviewed_after ||
  !!f.reviewed_before ||
  !!f.validity_state_in ||
  !!f.alert_status_in ||
  !!f.alert_type_in ||
  !!f.alert_after ||
  !!f.alert_before ||
  f.require_valid_flag === true;

/**
 * ──────────────────────────────────────────────────────────────────────
 * Classification Review — filter & scope state
 * ──────────────────────────────────────────────────────────────────────
 *
 * State model (intentional, please keep):
 *
 *   appliedFilters  — SINGLE source of truth for what the backend sees.
 *                     Every chip in the filter bar ultimately calls
 *                     setAppliedFilters to commit its draft. React Query
 *                     keys off this object, so each mutation of it = 1
 *                     backend request. Nothing else drives the query. All
 *                     filter chips are shown inline at all times; an unset
 *                     chip simply contributes no key to this object.
 *
 *   scope           — location + machineIds, committed atomically via
 *                     the Scope chip's Apply button. Lives separately
 *                     from appliedFilters.label_in/etc because swapping
 *                     scope is a much bigger event (new dataset, new
 *                     session, cleared selection).
 *
 * The rule: NO state change outside of an explicit commit action
 * (Apply scope / close popover / slider release / blur) should ever
 * change appliedFilters. Dragging a slider, clicking checkboxes inside
 * a popover, typing in a datetime input — none of these should fire
 * requests. This is how we keep the backend at 1–3 requests per user
 * action instead of 10–50.
 */
export const useReviewFilters = () => {
  const { value: dateRange, setValue: setDateRange } = useDateTimeRangeStore();
  const clearSelection = useBulkSelectionStore((s) => s.clear);

  // ─── Live state from the persisted store ──────────────────────────
  const selectedLocationId = useReviewFilterStore((s) => s.selectedLocationId);
  const selectedMachineIdsArr = useReviewFilterStore((s) => s.selectedMachineIds);
  const appliedFilters = useReviewFilterStore((s) => s.appliedFilters);
  const savedPresets = useReviewFilterStore((s) => s.savedPresets);
  const recentHistory = useReviewFilterStore((s) => s.recentHistory);

  // Expose a Set to consumers (unchanged public shape).
  const selectedMachineIds = useMemo(
    () => new Set(selectedMachineIdsArr),
    [selectedMachineIdsArr],
  );

  const machineIdsParam = selectedMachineIdsArr.join(",");
  const hasScopeSelected = !!selectedLocationId || !!machineIdsParam;

  const activeFilters: ReviewFilters = useMemo(() => {
    const start = dateRange.fromDate;
    let end = dateRange.toDate;
    // Guard: backend rejects end_time <= start_time.
    if (end.getTime() <= start.getTime()) {
      end = new Date(start.getTime() + 24 * 60 * 60 * 1000); // +24h
    }
    return {
      ...appliedFilters,
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      location_id: machineIdsParam ? undefined : selectedLocationId,
      machine_ids: machineIdsParam || undefined,
    };
  }, [appliedFilters, dateRange, selectedLocationId, machineIdsParam]);

  // Single definition — the filter bar takes this as a prop rather than
  // recomputing it, which is how the two copies used to drift apart.
  const hasRefinement = hasAnyRefinement(appliedFilters);

  // ─── Snapshot helpers ─────────────────────────────────────────────
  const getSnapshot = useCallback((): ReviewFilterSnapshot => {
    const f = useReviewFilterStore.getState();
    const d = useDateTimeRangeStore.getState().value;
    return {
      selectedLocationId: f.selectedLocationId,
      selectedMachineIds: [...f.selectedMachineIds],
      dateRange: {
        fromDate: d.fromDate.toISOString(),
        toDate: d.toDate.toISOString(),
      },
      appliedFilters: { ...f.appliedFilters },
    };
  }, []);

  // Record the current committed view into history (deduped/capped by the
  // store). Only meaningful once a scope is selected.
  const recordHistoryNow = useCallback(() => {
    const f = useReviewFilterStore.getState();
    if (!f.selectedLocationId && f.selectedMachineIds.length === 0) return;
    f.recordHistory(getSnapshot());
  }, [getSnapshot]);

  // ─── Handlers (same API as before) ────────────────────────────────
  const handleApplyFilters = useCallback(
    (patch: Partial<ReviewFilters>) => {
      const store = useReviewFilterStore.getState();
      const next = { ...store.appliedFilters, ...patch };
      (Object.keys(next) as Array<keyof typeof next>).forEach((k) => {
        if (next[k] === undefined) delete next[k];
      });
      store.setAppliedFilters(next);
      recordHistoryNow();
    },
    [recordHistoryNow],
  );

  const handleClearAllFilters = useCallback(() => {
    useReviewFilterStore.getState().setAppliedFilters({ limit: 100 });
  }, []);

  /** Clear a single refinement's value(s), removing its chip from the bar. */
  const handleClearFilter = useCallback(
    (key: RevealedFilterKey) => {
      const patch: Partial<ReviewFilters> = {};
      if (key === "label") patch.label_in = undefined;
      if (key === "state") patch.review_state_in = undefined;
      if (key === "event_state") patch.event_state_in = undefined;
      if (key === "presence") patch.review_presence = undefined;
      if (key === "confidence") {
        patch.min_confidence = undefined;
        patch.max_confidence = undefined;
      }
      if (key === "reviewer") patch.reviewed_by = undefined;
      if (key === "reviewed_in") {
        patch.reviewed_after = undefined;
        patch.reviewed_before = undefined;
      }
      if (key === "alert") patch.alert_status_in = undefined;
      if (key === "alert_type") patch.alert_type_in = undefined;
      if (key === "event_time") {
        patch.alert_after = undefined;
        patch.alert_before = undefined;
      }
      if (key === "validity") {
        // Clear both the tri-state and the legacy shortcut so the chip resets.
        patch.validity_state_in = undefined;
        patch.require_valid_flag = undefined;
      }
      handleApplyFilters(patch);
    },
    [handleApplyFilters],
  );

  const handleApplyScope = useCallback(
    (locationId: number | undefined, machineIds: Set<number>) => {
      const store = useReviewFilterStore.getState();
      store.setSelectedLocationId(locationId);
      store.setSelectedMachineIds([...machineIds]);
      clearSelection();
      recordHistoryNow();
    },
    [clearSelection, recordHistoryNow],
  );

  // ─── Presets ──────────────────────────────────────────────────────
  const applySnapshot = useCallback(
    (snapshot: ReviewFilterSnapshot) => {
      const store = useReviewFilterStore.getState();
      store.setSelectedLocationId(snapshot.selectedLocationId);
      store.setSelectedMachineIds([...snapshot.selectedMachineIds]);
      store.setAppliedFilters({ ...snapshot.appliedFilters });
      setDateRange({
        fromDate: new Date(snapshot.dateRange.fromDate),
        toDate: new Date(snapshot.dateRange.toDate),
      });
      clearSelection();
    },
    [clearSelection, setDateRange],
  );

  const savePreset = useCallback(
    (name: string) => {
      useReviewFilterStore.getState().savePreset(name, getSnapshot());
    },
    [getSnapshot],
  );

  const deletePreset = useCallback((id: string) => {
    useReviewFilterStore.getState().deletePreset(id);
  }, []);

  return {
    selectedLocationId,
    selectedMachineIds,
    appliedFilters,
    machineIdsParam,
    hasScopeSelected,
    hasRefinement,
    activeFilters,
    handleApplyFilters,
    handleClearAllFilters,
    handleClearFilter,
    handleApplyScope,
    // New: presets + history
    presets: savedPresets,
    history: recentHistory,
    getSnapshot,
    applySnapshot,
    savePreset,
    deletePreset,
  };
};
